import { Administration, Ledger, VatType } from '@cbgms/api'
import { WorkorderInvoiceType } from '@cbgms/api/modules/enums/workorder-invoice-type'
import { EmptyUUID } from '@cbgms/base/utils/uuid'
import { StructuredDepartments } from './types.d'

export const vatSortOrder = ['high', 'low_one', 'low_two', 'low_three', 'zero', 'intra_community', 'outside_eu', 'margin', 'none']

export const sortLedgersOnVATType = (ledgerA: { VatType: VatType }, ledgerB: { VatType: VatType }) => {
  const vatAIndex = vatSortOrder.findIndex(vatOrder => vatOrder === ledgerA.VatType)
  const vatBIndex = vatSortOrder.findIndex(vatOrder => vatOrder === ledgerB.VatType)
  return vatAIndex - vatBIndex
}

export const sortVATTypes = (vatType1: VatType, vatType2: VatType) => {
  const vatAIndex = vatSortOrder.findIndex(vatOrder => vatOrder === vatType1)
  const vatBIndex = vatSortOrder.findIndex(vatOrder => vatOrder === vatType2)
  return vatAIndex - vatBIndex
}

export const getFallbackIndex = (administration: Administration) =>
  administration.Ledgers.findIndex(ledger => ledger.DepartmentUUID === EmptyUUID || ledger.DepartmentUUID === '')

export const getInitialLedgerValues = (
  administration: Administration,
  rest: {
    VatType: VatType
    InvoiceType: WorkorderInvoiceType
    RevenueGroupUUID?: string
    DepartmentUUID?: string
    DepartmentName?: string
    IsRevenueFieldRequired?: boolean
    IsDiscountFieldRequired?: boolean
  }
): Ledger => ({
  AccountType: '',
  Revenue: '',
  Discount: '',
  Fees: '',
  CostPrice: '',
  Balance: '',
  DepartmentUUID: EmptyUUID,
  RevenueGroupUUID: '',
  AdministrationUUID: administration.UUID,
  AdministrationExternalID: administration.AdministrationExternalID,
  AdministrationCode: administration.AdministrationCode,
  ...rest
})

export const generateAdministrationWithMissingLedgers = (
  administration: Administration,
  vatLimiter: VatType[],
  departments: StructuredDepartments,
  revenueGroupUUID = '',
  administrationAllDeafultLedger?: Ledger
) => {
  const currentLedgers = administration?.Ledgers || []
  const generatedLedgers: Ledger[] = [
    ...currentLedgers.filter(
      ledger => vatLimiter.some(vatType => ledger.VatType === vatType) || ledger.InvoiceType !== WorkorderInvoiceType.Standard
    )
  ]

  if (generatedLedgers.length === 0 || !generatedLedgers.some(ledger => ledger.InvoiceType === WorkorderInvoiceType.Warranty)) {
    generatedLedgers.push(
      getInitialLedgerValues(administration, {
        VatType: VatType.None,
        InvoiceType: WorkorderInvoiceType.Warranty
      })
    )
  }

  if (generatedLedgers.length === 0 || !generatedLedgers.some(ledger => ledger.InvoiceType === WorkorderInvoiceType.Internal)) {
    generatedLedgers.push(
      getInitialLedgerValues(administration, {
        VatType: VatType.None,
        InvoiceType: WorkorderInvoiceType.Internal
      })
    )
  }

  vatLimiter.forEach((vatType: VatType) => {
    const ledgersExist = generatedLedgers.some(
      ledger => ledger.DepartmentUUID !== EmptyUUID && ledger.DepartmentUUID !== '' && ledger.VatType === vatType
    )
    const fallbackLedger = generatedLedgers.find(
      ledger => (ledger.DepartmentUUID === EmptyUUID || ledger.DepartmentUUID === '') && ledger.VatType === vatType
    )

    if (!fallbackLedger) {
      generatedLedgers.push(
        getInitialLedgerValues(administration, {
          VatType: vatType,
          RevenueGroupUUID: revenueGroupUUID,
          InvoiceType: WorkorderInvoiceType.Standard
        })
      )
    }

    if (!ledgersExist && administration.UUID === EmptyUUID) {
      departments.forEach(department => {
        generatedLedgers.push(
          getInitialLedgerValues(administration, {
            VatType: vatType,
            DepartmentUUID: department.uuid,
            DepartmentName: department.name,
            RevenueGroupUUID: revenueGroupUUID,
            InvoiceType: WorkorderInvoiceType.Standard,
            IsRevenueFieldRequired: false,
            IsDiscountFieldRequired: false
          })
        )
      })
    } else if (!ledgersExist && administration.UUID !== EmptyUUID && administration.UUID !== '') {
      departments.forEach(department => {
        generatedLedgers.push(
          getInitialLedgerValues(administration, {
            VatType: vatType,
            DepartmentUUID: department.uuid,
            DepartmentName: department.name,
            RevenueGroupUUID: revenueGroupUUID,
            InvoiceType: WorkorderInvoiceType.Standard,
            IsRevenueFieldRequired: !(!!administrationAllDeafultLedger?.Revenue || !!fallbackLedger?.Revenue),
            IsDiscountFieldRequired: !(!!administrationAllDeafultLedger?.Discount || !!fallbackLedger?.Discount)
          })
        )
      })
    } else {
      departments.forEach(department => {
        const ledgerForDepartmentExists = generatedLedgers.some(
          ledger => (ledger.DepartmentUUID === department.uuid || ledger.DepartmentUUID === '') && ledger.VatType === vatType
        )

        if (!ledgerForDepartmentExists && fallbackLedger) {
          generatedLedgers.push(
            getInitialLedgerValues(administration, {
              VatType: vatType,
              DepartmentUUID: department.uuid,
              DepartmentName: department.name,
              RevenueGroupUUID: revenueGroupUUID,
              InvoiceType: WorkorderInvoiceType.Standard,
              IsRevenueFieldRequired: !administrationAllDeafultLedger?.Revenue,
              IsDiscountFieldRequired: !administrationAllDeafultLedger?.Discount
            })
          )
        }
      })
    }
  })

  const sortedGeneratedLedgers = generatedLedgers.sort(sortLedgersOnVATType)

  return { ...administration, Ledgers: sortedGeneratedLedgers }
}
